import { ChangeDetectionStrategy, Component, OnInit, Signal, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { ApiService } from '../../../core/services/api.service';
import { AuthService } from '../../../core/services/auth.service';
import { DatabaseService } from '../../../core/services/database.service';
import { LoggerService } from '../../../core/services/logger.service';
import { NetworkStateService } from '../../../core/services/network-state.service';
import { OrganizationData, OrganizationService } from '../../../core/services/organization.service';
import { ToastService } from '../../../core/services/toast.service';
import { TranslationService } from '../../../core/services/translation.service';
import { BackButtonComponent } from '../../../shared/components/back-button/back-button.component';
import { ButtonComponent } from '../../../shared/components/button/button.component';
import { CardContentComponent } from '../../../shared/components/card/card-content.component';
import { CardComponent } from '../../../shared/components/card/card.component';
import { FormFieldComponent } from '../../../shared/components/form-controls/form-field/form-field.component';
import { InputComponent } from '../../../shared/components/form-controls/input/input.component';
import { IconComponent } from '../../../shared/components/icon/icon.component';
import { ModalDialogComponent } from '../../../shared/components/modal-dialog/modal-dialog.component';
import { OrganizationListComponent } from '../../../shared/components/organization-list/organization-list.component';
import { TranslatePipe } from '../../../shared/pipes/translate.pipe';

/** Organization name display + rename (owners only), plus the switch/create/
 *  delete organization list (app-organization-list) -- this page absorbed the
 *  old standalone /organizations nav item; that route still exists for the
 *  no-active-org redirect case (see organizations.component) but is no longer
 *  linked from the nav. */
@Component({
  selector: 'app-organization-settings',
  standalone: true,
  imports: [
    FormsModule,
    BackButtonComponent,
    CardComponent,
    CardContentComponent,
    IconComponent,
    ButtonComponent,
    FormFieldComponent,
    InputComponent,
    ModalDialogComponent,
    OrganizationListComponent,
    TranslatePipe,
  ],
  templateUrl: './organization-settings.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./organization-settings.component.css', '../../../../css/settings-shared.css'],
})
export class OrganizationSettingsComponent implements OnInit {
  private auth = inject(AuthService);
  private api = inject(ApiService);
  private db = inject(DatabaseService);
  private toast = inject(ToastService);
  private translate = inject(TranslationService);
  private organizationService = inject(OrganizationService);
  private networkState = inject(NetworkStateService);
  private logger = inject(LoggerService).getLogger('organization-settings');

  readonly isOnline = this.networkState.isOnline;
  readonly stayOfflineMode = this.networkState.stayOfflineMode;
  readonly canUseNetwork = computed(() => this.isOnline() && !this.stayOfflineMode());

  organizationData!: Signal<OrganizationData | null>;
  organizationName!: Signal<string | null>;
  readonly userRole = signal<string | null>(null);
  readonly showRenameDialog = signal(false);
  newOrganizationName = signal('');
  readonly renamingOrganization = signal(false);
  readonly organizationNameChanged = computed(() => {
    const current = this.organizationName();
    const newName = this.newOrganizationName().trim();
    return newName !== '' && newName !== current;
  });

  async ngOnInit() {
    const token = this.auth.getToken();
    let currentCustomerId: string | undefined;
    if (token) {
      const payload = this.auth.decodeJwt(token);
      currentCustomerId = payload.customer_id;

      const query = this.organizationService.createOrganizationQuery(currentCustomerId);
      this.organizationData = query.data;
    } else {
      this.organizationData = signal(null);
    }
    this.organizationName = computed(() => this.organizationData()?.name || null);

    if (token && currentCustomerId) {
      try {
        const response = await fetch(`${this.api['apiUrl']}/customers/my-organizations`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          const currentOrg = data.organizations?.find(
            (org: any) => org.customer_id === currentCustomerId,
          );

          if (currentOrg) {
            this.userRole.set(currentOrg.role);
          }
        }
      } catch (error) {
        this.logger.error('Failed to fetch user role:', error);
      }
    }
  }

  showRenameOrganization() {
    if (!this.canUseNetwork()) {
      this.toast.error(this.translate.translate('settings.organization.renameOfflineError'));
      return;
    }

    this.newOrganizationName.set(this.organizationName() || '');
    this.showRenameDialog.set(true);
  }

  cancelRename() {
    this.showRenameDialog.set(false);
    this.newOrganizationName.set('');
  }

  async confirmRename() {
    const newName = this.newOrganizationName().trim();

    if (!newName) {
      this.toast.error(this.translate.translate('settings.organization.nameRequired'));
      return;
    }

    if (newName.length < 2) {
      this.toast.error(this.translate.translate('settings.organization.nameTooShort'));
      return;
    }

    if (newName === this.organizationName()) {
      this.cancelRename();
      return;
    }

    try {
      this.renamingOrganization.set(true);

      const token = this.auth.getToken();
      if (!token) {
        throw new Error('Not authenticated');
      }

      const payload = this.auth.decodeJwt(token);
      const customerId = payload.customer_id;

      await firstValueFrom(this.api.updateOrganizationName(customerId, newName));

      this.toast.success(this.translate.translate('settings.organization.renameSuccess'));
      this.showRenameDialog.set(false);
      this.newOrganizationName.set('');

      // Manually trigger table refresh since sync changes don't always trigger onUpdate hooks
      this.db.notifyDataChange('organization');
    } catch (error) {
      this.logger.error('Failed to rename organization:', error);
      this.toast.error(this.translate.translate('settings.organization.renameFailed'));
    } finally {
      this.renamingOrganization.set(false);
    }
  }
}
